import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DesignDataCell } from '@/components/result/DesignDataCell';
import { CommentToggleButton, ExpandableCommentTableRows } from './CommentExpand';
import { ScenarioResultBadge } from './ScenarioResultBadge';
import { MotorLimbValueCell } from '@/utils/evaluationResultUtils';
import { formatGenderAge, isScenarioAnswerCorrect } from '@/utils/scenarioResultFormat';
import { AssessmentTableShell } from './AssessmentResult';

export function MotorAssessmentTable({ title, rows }: { title: string; rows: any[] }): React.ReactElement | null {
  const { t } = useTranslation('class');
  const { t: tScenario } = useTranslation('scenario');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  if (!rows?.length) {
    return (
      <AssessmentTableShell title={title}>
        <table className="w-full text-sm text-center" role="presentation">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableNo')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('gcsEvaluationResult.tableName')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableGenderAge')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableLeftArm')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableRightArm')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableLeftLeg')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableRightLeg')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableResult')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableComment')}</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((r) => (
              <tr className="border-b border-slate-100/80" key={`motor-skel-${r}`}>
                <td className="px-4 py-3 text-center text-slate-500 font-medium align-top">{r + 1}</td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="이름" align="left" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="성/연" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="L·팔" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="R·팔" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="L·다리" align="center" /></td>
                <td className="px-4 py-3 align-top"><DesignDataCell hint="R·다리" align="center" /></td>
                <td className="px-4 py-3 align-top text-center text-slate-500"><DesignDataCell hint="O/X" align="center" /></td>
                <td className="px-4 py-3 align-top text-slate-400 text-center">···</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AssessmentTableShell>
    );
  }

  return (
    <AssessmentTableShell title={title}>
      <table className="w-full text-sm text-center">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableNo')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('gcsEvaluationResult.tableName')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableGenderAge')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableLeftArm')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableRightArm')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableLeftLeg')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('motorPowerResult.tableRightLeg')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('gcsEvaluationResult.tableResult')}</th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('pupilReactionResult.tableComment')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: any, idx: number) => {
            const isExpanded = expandedRow === idx;
            const toggleComment = () => setExpandedRow((prev) => (prev === idx ? null : idx));
            const isCompleted = isScenarioAnswerCorrect(row.completed_missed);
            return (
              <ExpandableCommentTableRows
                key={idx}
                isExpanded={isExpanded}
                toggleComment={toggleComment}
                colSpan={8}
                comment={row.comment}
              >
                <td className="px-4 py-3 text-center text-gray-900">{row.no ?? idx + 1}</td>
                <td className="px-4 py-3 text-start text-gray-900">{row.name ?? '-'}</td>
                <td className="px-4 py-3 text-center text-gray-600">{formatGenderAge(row.gender, row.age)}</td>
                <td className="px-4 py-3 text-center text-gray-700"><MotorLimbValueCell row={row} limbKey="left_arm" /></td>
                <td className="px-4 py-3 text-center text-gray-700"><MotorLimbValueCell row={row} limbKey="right_arm" /></td>
                <td className="px-4 py-3 text-center text-gray-700"><MotorLimbValueCell row={row} limbKey="left_leg" /></td>
                <td className="px-4 py-3 text-center text-gray-700"><MotorLimbValueCell row={row} limbKey="right_leg" /></td>
                <td className="px-4 py-3 text-center">
                  {row.completed_missed ? (
                    <ScenarioResultBadge
                      ok={isCompleted}
                      labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
                      labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-4 py-3 text-center text-gray-600">
                  <CommentToggleButton
                    isExpanded={isExpanded}
                    onToggle={toggleComment}
                    ariaLabel={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
                  />
                </td>
              </ExpandableCommentTableRows>
            );
          })}
        </tbody>
      </table>
    </AssessmentTableShell>
  );
}

interface MotorResultProps {
  title: string;
  resultMetadata: any;
}

/** content_code `motor_power` 전용 표 (상단 요약은 SimulationResults `EvalSummaryStatGrid`) */
export default function MotorResult({ title, resultMetadata }: MotorResultProps): React.ReactElement | null {
  return <MotorAssessmentTable title={title} rows={resultMetadata?.motorPowerPerformance} />;
}
